import { createClient } from '@supabase/supabase-js';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/integrations/supabase/types';

function readEnv(...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = typeof process !== 'undefined' ? process.env[key] : undefined;
    if (value && value.trim().length > 0) {
      return value.trim();
    }
  }

  if (typeof import.meta !== 'undefined' && typeof import.meta.env === 'object') {
    const env = import.meta.env as Record<string, string | undefined>;
    for (const key of keys) {
      const direct = env[key];
      if (direct && direct.trim().length > 0) {
        return direct.trim();
      }

      if (key.startsWith('NEXT_PUBLIC_')) {
        const fallbackKey = `VITE_${key.slice('NEXT_PUBLIC_'.length)}`;
        const fallback = env[fallbackKey];
        if (fallback && fallback.trim().length > 0) {
          return fallback.trim();
        }
      } else if (key.startsWith('VITE_')) {
        const fallbackKey = `NEXT_PUBLIC_${key.slice('VITE_'.length)}`;
        const fallback = env[fallbackKey];
        if (fallback && fallback.trim().length > 0) {
          return fallback.trim();
        }
      }
    }
  }

  return undefined;
}

function requireEnv(label: string, keys: string[]): string {
  const value = readEnv(...keys);
  if (!value) {
    throw new Error(`Missing ${label}. Set ${keys.join(' or ')} in the environment.`);
  }
  return value;
}

const SUPABASE_URL = requireEnv('NEXT_PUBLIC_SUPABASE_URL', [
  'NEXT_PUBLIC_SUPABASE_URL',
  'VITE_SUPABASE_URL',
]);
const SUPABASE_ANON_KEY = requireEnv('NEXT_PUBLIC_SUPABASE_ANON_KEY', [
  'NEXT_PUBLIC_SUPABASE_ANON_KEY',
  'VITE_SUPABASE_ANON_KEY',
]);

type BrowserClient = SupabaseClient<Database>;

let browserClient: BrowserClient | undefined;

function createBrowserClient(): BrowserClient {
  return createClient<Database>(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'implicit',
    },
  });
}

export const supabaseBrowser: BrowserClient = (() => {
  if (browserClient) return browserClient;

  if (typeof window !== 'undefined') {
    const globalKey = '__supabaseBrowser';
    const existing = (window as typeof window & { __supabaseBrowser?: BrowserClient })[globalKey];
    if (existing) {
      browserClient = existing;
      return browserClient;
    }

    browserClient = createBrowserClient();
    (window as typeof window & { __supabaseBrowser?: BrowserClient })[globalKey] = browserClient;
    
    // 🔑 Синхронизируем токен с Functions на старте
    (async () => {
      const { data } = await browserClient.auth.getSession();
      browserClient.functions.setAuth(data.session?.access_token ?? null);
    })();

    // 🔁 И обновляем при любом изменении сессии (login/refresh/logout)
    browserClient.auth.onAuthStateChange((_event, session) => {
      browserClient.functions.setAuth(session?.access_token ?? null);
    });
    
    return browserClient;
  }

  browserClient = createBrowserClient();
  return browserClient;
})();
